import { useState } from "react";
import { motion } from "framer-motion";
import { PROFILES, OTTOSTUMM_LOGO_DARK } from "@/constants/assets";

export const ProfilesSection = () => {
  const [hovered, setHovered] = useState(null);

  return (
    <section
      id="profiles"
      data-testid="profiles-section"
      className="relative overflow-hidden bg-white pt-24 pb-14 lg:pt-36 lg:pb-20"
    >
      <div className="mx-auto max-w-[1600px] px-6 lg:px-16">
        {/* Header row — Ottostumm | Mogs logo + section eyebrow */}
        <div className="flex flex-col items-start gap-10 lg:flex-row lg:items-end lg:justify-between">
          <div className="flex flex-col gap-4">
            <motion.img
              src={OTTOSTUMM_LOGO_DARK}
              alt="Ottostumm | Mogs"
              data-testid="ottostumm-logo"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, ease: [0.19, 1, 0.22, 1] }}
              className="h-12 w-auto object-contain lg:h-16"
              style={{ filter: "brightness(0) saturate(100%)" }}
            />
          </div>

          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.05, ease: [0.19, 1, 0.22, 1] }}
            className="font-serif-display max-w-xl text-3xl font-light leading-[1.05] text-black lg:text-right lg:text-[44px]"
          >
            The FerroFinestra system, profile by profile.
          </motion.h2>
        </div>

        {/* Profile grid */}
        <div
          data-testid="profiles-grid"
          onMouseLeave={() => setHovered(null)}
          className="mt-20 grid grid-cols-1 gap-10 sm:grid-cols-2 lg:mt-28 lg:grid-cols-5 lg:gap-8"
        >
          {PROFILES.map((p, i) => {
            const isDim = hovered !== null && hovered !== p.code;
            const isActive = hovered === p.code;
            return (
              <motion.article
                key={p.code}
                data-testid={`profile-card-${p.code.toLowerCase()}`}
                onMouseEnter={() => setHovered(p.code)}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{
                  duration: 0.9,
                  delay: 0.1 + i * 0.08,
                  ease: [0.19, 1, 0.22, 1],
                }}
                className="group relative"
              >
                <div
                  data-testid={`profile-card-inner-${p.code.toLowerCase()}`}
                  className="flex flex-col items-start"
                  style={{
                    opacity: isDim ? 0.55 : 1,
                    transform: isActive ? "translateY(-8px)" : "translateY(0)",
                    transition:
                      "opacity 500ms cubic-bezier(0.19,1,0.22,1), transform 600ms cubic-bezier(0.19,1,0.22,1)",
                  }}
                >
                {/* Image plate */}
                <div className="relative flex h-[380px] w-full items-center justify-center overflow-hidden lg:h-[440px]">
                  <img
                    src={p.image}
                    alt={p.name}
                    draggable={false}
                    className="max-h-full max-w-full select-none object-contain"
                    style={{
                      transform: isActive ? "scale(1.12)" : "scale(1)",
                      transition:
                        "transform 700ms cubic-bezier(0.19,1,0.22,1)",
                      filter:
                        "drop-shadow(0 40px 40px rgba(0,0,0,0.10)) drop-shadow(0 10px 14px rgba(0,0,0,0.06))",
                    }}
                  />
                </div>

                {/* Divider */}
                <div className="mt-6 h-px w-full bg-black/15" />

                {/* Meta */}
                <div className="mt-5 flex w-full items-start justify-between gap-4">
                  <div>
                    <p className="font-tech text-[11px] font-bold uppercase tracking-[0.32em] text-[#c6a87c]">
                      {p.code}
                    </p>
                    <h3 className="font-serif-display mt-3 text-2xl font-bold leading-tight text-black lg:text-[28px]">
                      {p.name}
                    </h3>
                    <p className="font-tech mt-2 text-[11px] font-semibold uppercase tracking-[0.26em] text-black/70">
                      {p.tagline}
                    </p>
                  </div>
                </div>

                <p className="mt-5 font-body text-[14px] font-medium leading-[1.7] text-black/85">
                  {p.description}
                </p>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};
